import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI, loginAttemptAPI } from '@/utils/api';
import { toast } from 'sonner';
import { trackLogin } from '@/utils/analytics';
import Header from '@/components/Header';

// Generate unique session ID for login attempt tracking
const getLoginSessionId = () => {
  let sessionId = sessionStorage.getItem('login_session_id');
  if (!sessionId) {
    sessionId = `login_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('login_session_id', sessionId);
  }
  return sessionId;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [dossierId, setDossierId] = useState(null);
  const [tempToken, setTempToken] = useState(null);

  useEffect(() => {
    // Récupérer les infos du dossier depuis sessionStorage
    const linkDossierId = sessionStorage.getItem('link_dossier_id');
    const linkTempToken = sessionStorage.getItem('link_temp_token');
    const linkEmail = sessionStorage.getItem('link_dossier_email');

    if (linkDossierId && linkTempToken) {
      setDossierId(linkDossierId);
      setTempToken(linkTempToken);
    }
    // Pré-remplir l'email : depuis le dossier/l'inscription (sessionStorage) ou
    // depuis le lien email (?email=), pour réduire la friction de connexion.
    const urlEmail = searchParams.get('email');
    const prefillEmail = linkEmail || urlEmail;
    if (prefillEmail) {
      setFormData((prev) => ({ ...prev, email: prefillEmail }));
    }
  }, []);

  // Save email on blur for login attempt tracking
  const handleEmailBlur = async (email) => {
    if (!email || !email.trim()) return;

    try {
      const sessionId = getLoginSessionId();
      await loginAttemptAPI.save({
        session_id: sessionId,
        email: email,
        user_agent: navigator.userAgent
      });
    } catch (error) {
      console.debug('Login attempt save error:', error);
    }
  };

  // Track login attempt result
  const trackLoginAttempt = async (success, errorMessage = null) => {
    try {
      const sessionId = getLoginSessionId();
      await loginAttemptAPI.save({
        session_id: sessionId,
        email: formData.email,
        attempted: true,
        success: success,
        error_message: errorMessage,
        user_agent: navigator.userAgent
      });

      // Reset session ID after tracking to allow new attempts
      if (success) {
        sessionStorage.removeItem('login_session_id');
      }
    } catch (error) {
      console.debug('Login attempt tracking error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData, dossierId, tempToken);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Nettoyer sessionStorage
      sessionStorage.removeItem('link_dossier_id');
      sessionStorage.removeItem('link_temp_token');
      sessionStorage.removeItem('link_dossier_email');

      toast.success('Connexion réussie !');

      // Track login attempt success
      await trackLoginAttempt(true);

      // Track login
      trackLogin('email');

      if (dossierId) {
        toast.success('Votre dossier a été lié à votre compte.');
        navigate(`/score/${dossierId}`);
      } else if (response.data.user.is_admin) {
        navigate('/admin');
      } else if (response.data.user.is_lawyer) {
        navigate('/lawyer');
      } else {
        navigate(redirectTo);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Email ou mot de passe incorrect';
      toast.error(errorMessage);

      // Track login attempt failure
      await trackLoginAttempt(false, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8fafc' }}>
      <Header />
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="w-full max-w-md px-4">
          <div className="card p-8">
          <div className="text-center mb-8">
            <Shield size={48} color="#1e3a8a" className="mx-auto mb-4" strokeWidth={2.5} />
            <h1 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
              Connexion
            </h1>
            <p className="text-gray-600 mt-2">Accédez à votre espace personnel</p>
          </div>

          <form data-testid="login-form" onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="input-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={(e) => handleEmailBlur(e.target.value)}
                placeholder="votre@email.fr"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="mt-2"
              />
            </div>

            <Button
              data-testid="submit-login-button"
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full"
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Pas encore de compte ?{' '}
              <Link
                to="/register"
                data-testid="register-link"
                className="font-semibold"
                style={{ color: '#1e3a8a' }}
              >
                S'inscrire
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/"
              data-testid="back-home-link"
              className="text-gray-500 hover:text-gray-700"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;